// Stav rozhraní: režim vzhledu (světlý výchozí, tmavý značkový, systém), online/offline a
// "aktualizuj aplikaci" (server vrátil 426 app_outdated).
import { defineStore } from 'pinia'
import { ref } from 'vue'

export type Theme = 'light' | 'dark' | 'system'

/** Server odmítl tuto verzi aplikace (426); minVersion je nejnižší přijímaná verze, když ji poslal. */
export interface OutdatedInfo {
  minVersion: string | null
}
const THEME_KEY = 'wac-buddy.theme'

function readTheme(): Theme {
  try {
    const v = localStorage.getItem(THEME_KEY)
    if (v === 'light' || v === 'dark' || v === 'system') return v
  } catch {
    /* soukromý režim bez úložiště */
  }
  return 'light'
}

function applyTheme(theme: Theme) {
  const root = document.documentElement
  if (theme === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', theme)
}

export const useUiStore = defineStore('ui', () => {
  const theme = ref<Theme>('light')
  const online = ref(true)
  const menuOpen = ref(false)
  /** Veřejné obrazovky (hostovaný formulář) sledují systémový režim bez ohledu na volbu v aplikaci. */
  const followSystem = ref(false)
  /** Nastaví klient API při odpovědi 426; celoobrazovková výzva k aktualizaci (OutdatedView). */
  const outdated = ref<OutdatedInfo | null>(null)

  function setOutdated(info: OutdatedInfo) {
    outdated.value = info
  }

  function setFollowSystem(on: boolean) {
    followSystem.value = on
    applyTheme(on ? 'system' : theme.value)
  }

  function setTheme(next: Theme) {
    theme.value = next
    applyTheme(followSystem.value ? 'system' : next)
    try {
      localStorage.setItem(THEME_KEY, next)
    } catch {
      /* ignorujeme */
    }
  }

  function init() {
    setTheme(readTheme())
    online.value = navigator.onLine
    window.addEventListener('online', () => (online.value = true))
    window.addEventListener('offline', () => (online.value = false))
  }

  return {
    theme,
    online,
    menuOpen,
    followSystem,
    outdated,
    setOutdated,
    setFollowSystem,
    setTheme,
    init,
  }
})
